<template>
  <div>
    <div v-if="physicalErrorMsg" class="app-alert-error app-system-alert">
      <div ref="alertMessage" tabindex="-1" class="bannerAlert">
        Physical Address - {{ physicalErrorMsg }}
      </div>
    </div>
    <div v-if="mailingErrorMsg" class="app-alert-error app-system-alert">
      <div ref="alertMessage" tabindex="-1" class="bannerAlert">
        Mailing Address - {{ mailingErrorMsg }}
      </div>
    </div>
    <div>
      <p>Your existing address is displayed below. To clear the existing address click 'Clear'.</p>
      <p>Please update your address and click on 'Next'. You can only update to an US Domestic address. The system will validate the address and may suggest a standardized address to ensure address accuracy that aids in timely mail delivery.</p>
      <button class="btnClear" @click="clearAll">
        Clear
      </button>
      <div class="phymailcheck">
        <input type="checkbox" id="address" v-model="physicalMailingAddressCheck" @change="checkMailingAddress()">
        <label for="address">My mailing address is same as my physical address</label>
      </div>
      <div class="app-address-info">
        <div>
          <h2 for="physicalAddress" aria-label="Physical address required" class="app-required-indicator __after">Physical Address</h2>
          <div class="app-input-address">
            <div class="app-user-address">
              <div class="add-line">
                <label for="phyaddressline1" class="sr-only">Address line 1</label>
                <input type="text" class="user-address-info" id="phyaddressline1" placeholder="Address line 1 *" v-model="physicalAddress1" @focusout="checkMailingAddress()"
                  @input="validateAddress">
                <p v-if="physicalAddress1ErrMsg" class="err-msg">{{ physicalAddress1ErrMsg }}</p>
              </div>
            </div>
            <div class="app-user-address">
              <div class="add-line">
                <label for="phyaddressline2" class="sr-only">Address line 2</label>
                <input type="text" placeholder="Address line 2" id="phyaddressline2" class="user-address-info" v-model="physicalAddress2" @focusout="checkMailingAddress()">
              </div>
            </div>
            <div class="app-user-address">
              <div v-if="physicalAddressState != 'RI' && physicalAddressState != 40">
                <label for="physicalAddressCityOut" class="sr-only">City</label>
                <input type="text" placeholder="City *" id="physicalAddressCityOut" class="user-address-info-city" v-model="physicalAddressCity" @focusout="checkMailingAddress()"
                  @input="validateCity">
                <p v-if="physicalAddressCityErrMsg" class="err-msg">{{ physicalAddressCityErrMsg }}</p>
              </div>
              <div v-else>
                <label for="physicalAddressCity" class="sr-only">Select a city</label>
                <select class="app-custom-select" id="physicalAddressCity" aria-required="true" v-model="physicalAddressCity" @focusout="checkMailingAddress()"
                @change="validateCity">
                  <option v-for="(type) in crashLocations" :value="type.description" :key="type.description">
                    {{ type.description == '' ? 'Select a city' : type.description }}
                  </option>
                </select>
                <p v-if="physicalAddressCityErrMsg" class="err-msg">{{ physicalAddressCityErrMsg }}</p>
              </div>
            </div>
            <div class="app-user-address-info">
              <div class="app-user-address">
                <div>
                  <label for="physicalAddressState" class="sr-only">Select state</label>
                  <select class="app-custom-select" id="physicalAddressState" aria-required="true" v-model="physicalStateId" @focusout="checkMailingAddress()"
                    @change="changeState">
                    <option value=''>Select state *</option>
                    <option v-for="(type) in usStates" :value="type.stateId" :key="type.stateId" :title="type.code">
                      {{ type.code }} - {{ type.description }}
                    </option>
                  </select>
                  <p v-if="physicalAdressStateErrMsg" class="err-msg">{{ physicalAdressStateErrMsg }}</p>
                </div>
              </div>
              <div>
                <div class="app-user-address">
                  <div v-if="physicalAddressState != 'RI' && physicalAddressState != 40">
                    <label for="physicalAddressZipOut" class="sr-only">Zipcode</label>
                    <input type="text" placeholder="Zip code *" id="physicalAddressZipOut" class="user-address-info-zip"
                      oninput="this.value = this.value.replace(/[^0-9]/g, '')" maxlength="5" v-model="physicalAddressZip" @focusout="checkMailingAddress()" @input="validateZip">
                  </div>
                  <div v-else>
                    <label for="physicalAddressZip" class="sr-only">Select a zipcode</label>
                    <select class="app-custom-select zip-selection" id="physicalAddressZip" aria-required="true" v-model="physicalAddressZip"
                      @focusout="checkMailingAddress()" @change="validateZip">
                      <option v-for="(type) in instateZipCodes" :value="type.zipCode" :key="type.zipId">
                        {{ type.zipCode == '' ? 'Zipcode' : type.zipCode }}
                      </option>
                    </select>
                  </div>
                  <div class="diff">-</div>
                  <div>
                    <label for="physicalAddressZipplus" class="sr-only">Zipplus</label>
                    <input type="text" placeholder="Zip plus" class="user-address-info-zipplus" id="physicalAddressZipplus"
                      oninput="this.value = this.value.replace(/[^0-9]/g, '')" maxlength="4" v-model="physicalAddressZipPlus" @focusout="checkMailingAddress()">
                  </div>
                </div>
                <p v-if="physicalAddressZipErrMsg" class="err-msg zip-error">{{ physicalAddressZipErrMsg }}</p>
              </div>
            </div>
          </div>
        </div>
        <div>
          <h2 for="mailingAddress" aria-label="Mailing address required" class="app-required-indicator __after">Mailing Address</h2>
          <div class="app-input-address">
            <div class="app-user-address">
              <div class="add-line">
                <label for="mailingaddressline1" class="sr-only">Address line 1</label>
                <input type="text" placeholder="Address line 1 *" id="mailingaddressline1" class="user-address-info" :class="{ 'user-address-info-gray': physicalMailingAddressCheck }"
                  v-model="mailingAddress1" :readonly="physicalMailingAddressCheck" :disabled="physicalMailingAddressCheck" @input="validateMailingAddress">
                <p v-if="mailingAddress1ErrMsg" class="err-msg">{{ mailingAddress1ErrMsg }}</p>
              </div>
            </div>
            <div class="app-user-address">
              <div class="add-line">
                <label for="mailingaddressline2" class="sr-only">Address line 2</label>
                <input type="text" placeholder="Address line 2" id="mailingaddressline2" class="user-address-info" :class="{ 'user-address-info-gray': physicalMailingAddressCheck }"
                  v-model="mailingAddress2" :readonly="physicalMailingAddressCheck" :disabled="physicalMailingAddressCheck">
              </div>
            </div>
            <div class="app-user-address">
              <div v-if="mailingAddressState != 'RI'&& mailingAddressState != 40">
                <label for="mailingAddressCityOut" class="sr-only">City</label>
                <input type="text" placeholder="City *" id="mailingAddressCityOut" class="user-address-info-city" :class="{ 'user-address-info-gray': physicalMailingAddressCheck }"
                  v-model="mailingAddressCity" :readonly="physicalMailingAddressCheck" :disabled="physicalMailingAddressCheck" @input="validateMailingCity">
                <p v-if="mailingAddressCityErrMsg" class="err-msg">{{ mailingAddressCityErrMsg }}</p>
                </div>
            <div v-else>
              <label for="mailingAddressCity" class="sr-only">Select a city</label>
              <select class="app-custom-select" aria-required="true" id="mailingAddressCity" :class="{ 'user-address-info-gray': physicalMailingAddressCheck }"
                v-model="mailingAddressCity" :readonly="physicalMailingAddressCheck" :disabled="physicalMailingAddressCheck" @change="validateMailingCity">
                <option v-for="(type) in crashLocations" :value=" type.description" :key="type.cityId">
                  {{ type.description == '' ? 'Select a city' : type.description }}
                </option>
              </select>
              <p v-if="mailingAddressCityErrMsg" class="err-msg">{{ mailingAddressCityErrMsg }}</p>
            </div>
          </div>
          <div class="app-user-address-info">
            <div class="app-user-address">
              <div>
                <label for="mailingAddressState" class="sr-only">Select state</label>
                <select class="app-custom-select" aria-required="true" id="mailingAddressState" :class="{ 'user-address-info-gray': physicalMailingAddressCheck }"
                  v-model="mailingStateId" :readonly="physicalMailingAddressCheck" :disabled="physicalMailingAddressCheck" @change="changeMailingState">
                  <option value=''>Select state *</option>
                  <option v-for="(type) in usStates" :value="type.stateId" :key="type.stateId" :title="type.code">
                    {{ type.code }} - {{ type.description }}
                  </option>
                </select>
                <p v-if="mailingAdressStateErrMsg" class="err-msg">{{ mailingAdressStateErrMsg }}</p>
              </div>
            </div>
            <div>
              <div class="app-user-address">
                <div v-if="mailingAddressState != 'RI' && mailingAddressState != 40">
                  <label for="mailingAddressZipOut" class="sr-only">Zipcode</label>
                  <input type="text" placeholder="Zip code *" id="mailingAddressZipOut" class="user-address-info-zip" :class="{ 'user-address-info-gray': physicalMailingAddressCheck }"
                    oninput="this.value = this.value.replace(/[^0-9]/g, '')" maxlength="5" v-model="mailingAddressZip" :readonly="physicalMailingAddressCheck"
                    :disabled="physicalMailingAddressCheck" @input="validateMailingZip">
                </div>
                <div v-else>
                  <label for="mailingAddressZip" class="sr-only">Select a zipcode</label>
                  <select class="app-custom-select zip-selection" aria-required="true" id="mailingAddressZip"
                    :class="{ 'user-address-info-gray': physicalMailingAddressCheck }" v-model="mailingAddressZip"
                    :readonly="physicalMailingAddressCheck" :disabled="physicalMailingAddressCheck" @change="validateMailingZip">
                    <option v-for="(type) in instateZipCodes" :value="type.zipCode" :key="type.zipId">
                      {{ type.zipCode =='' ? 'Zipcode' : type.zipCode }}
                    </option>
                  </select>
                </div>
                <div class="diff">-</div>
                <div>
                  <label for="mailingAddressZipplus" class="sr-only">Zipplus</label>
                  <input type="text" placeholder="Zip plus" id="mailingAddressZipplus" oninput="this.value = this.value.replace(/[^0-9]/g, '')" maxlength="4"
                    class="user-address-info-zipplus" :class="{ 'user-address-info-gray': physicalMailingAddressCheck }" v-model="mailingAddressZipPlus"
                    :readonly="physicalMailingAddressCheck" :disabled="physicalMailingAddressCheck">
                </div>
              </div>
              <p v-if="mailingAddressZipErrMsg" class="err-msg zip-error">{{ mailingAddressZipErrMsg }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
    <BaseButtonGroup>
      <button type="button" @click="handleRouting('previous')">Previous</button>
      <CustomButton @click="handleRouting('next')">
        Next
      </CustomButton>
    </BaseButtonGroup>
  </div>
</template>


<script setup>
import { ref, computed, onMounted, nextTick, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router'; 
import * as COMMONS from '../../../store/_commons.js';
import BaseButtonGroup from '@/components/ui/BaseButtonGroup';
import CustomButton from '../../CustomButton.vue';

// Store 
const store = useStore();

const router = useRouter();

// Reactive State 
const physicalAddress1 = ref('');
const physicalAddress2 = ref('');
const physicalAddressCity = ref('');
const physicalAddressState = ref('');
const physicalAddressZip = ref('');
const physicalAddressZipPlus = ref('');

const mailingAddress1 = ref('');
const mailingAddress2 = ref('');
const mailingAddressCity = ref('');
const mailingAddressState = ref('');
const mailingAddressZip = ref('');
const mailingAddressZipPlus = ref('');

const physicalMailingAddressCheck = ref(true);

const physicalAddress1ErrMsg = ref('');
const physicalAddressCityErrMsg = ref('');
const physicalAdressStateErrMsg = ref('');
const physicalAddressZipErrMsg = ref('');

const mailingAddress1ErrMsg = ref('');
const mailingAddressCityErrMsg = ref('');
const mailingAdressStateErrMsg = ref('');
const mailingAddressZipErrMsg = ref('');

// Computed Properties 
// const personalInfo = computed(() => store.getters[COMMONS.GUEST_PERSONAL_INFO]);

const businessInfo = computed(() => store.getters[COMMONS.BUSINESS_PROFILE_INFO]);
const crashLookupInfo = computed(() => store.getters[COMMONS.GUEST_LOGIN_LOOKUP_INFO]);
const addressLookupInfo = computed(() => store.getters[COMMONS.GUEST_ADDRESS_LOOKUP_INFO]);

const checkSameAddress = computed(() => {
  return (
    physicalAddress1.value == mailingAddress1.value &&
    physicalAddress2.value == mailingAddress2.value &&
    physicalAddressCity.value == mailingAddressCity.value &&
    physicalAddressState.value == mailingAddressState.value &&
    physicalAddressZip.value == mailingAddressZip.value &&
    physicalAddressZipPlus.value == mailingAddressZipPlus.value
  );
});

const crashLocations = computed(() => {
  let sortCityName = [];
  if (crashLookupInfo.value.allCrashLocation) {
    sortCityName = crashLookupInfo.value.allCrashLocation.slice().sort((a, b) => {
      return a.description.localeCompare(b.description);
    });
  }
  return sortCityName;
});

const usStates = computed(() => {
  return addressLookupInfo.value.allUsStates;
});

const instateZipCodes = computed(() => {
  return addressLookupInfo.value.allInstateZipcodes;
});

const physicalStateId = computed({
  get() {
    if (addressLookupInfo.value?.allUsStates) {
      const stateCode = physicalAddressState.value;
      const stateArray = addressLookupInfo.value.allUsStates.filter((val) => {
        return stateCode == val.code || stateCode == val.stateId;
      });
      return stateArray.length > 0 ? stateArray[0].stateId : '';
    } else {
      return '';
    }
  },
  set(newVal) {
    physicalAddressState.value = newVal;
  },
});

//Computed property for `mailingStateId`
const mailingStateId = computed({
  get() {
    if (addressLookupInfo.value?.allUsStates) {
      const stateCode = mailingAddressState.value;
      const stateArray = addressLookupInfo.value.allUsStates.filter((val) => {
        return stateCode == val.code || stateCode == val.stateId;
      });
      return stateArray.length > 0 ? stateArray[0].stateId : '';
    } else {
      return '';
    }
  },
  set(newVal) {
    mailingAddressState.value = newVal;
  },
});

const physicalErrorMsg = computed(() => store.state.guestLogin.physicalAddressMsg);
const mailingErrorMsg = computed(() => store.state.guestLogin.mailingAddressMsg);

const getCrashLocation = () => {
  store.dispatch(COMMONS.GUEST_CRASH_LOCATION);
};

const getUsStates = () => {
  store.dispatch(COMMONS.GUEST_US_STATES);
};

const getInstateZipCodes = () => {
  store.dispatch(COMMONS.GUEST_INSTATE_ZIPCODES);
};

getCrashLocation();
getUsStates();
getInstateZipCodes();

// Populate form data if `businessInfo` is available
if (businessInfo.value?.businessPersonalInformation) {
  const { physicalAddress, mailingAddress } = businessInfo.value.businessPersonalInformation;
  
  if (physicalAddress) {
    physicalAddress1.value = physicalAddress.addressLine1 || '';
    physicalAddress2.value = physicalAddress.addressLine2 || '';
    physicalAddressCity.value = physicalAddress.city?.toUpperCase() || '';
    physicalAddressState.value = physicalAddress.addressType == 'Us Domestic' ? physicalAddress.state : physicalAddress.province;

    if (physicalAddress.addressType === 'Us Domestic') {
      physicalAddressZip.value = physicalAddress.zipCode || '';
      physicalAddressZipPlus.value = physicalAddress.zipPlus || '';
    } 
    else {
      let intlZip = '';

      if (physicalAddress.addressType === 'Us Domestic') {
        intlZip = physicalAddress.zipPlus === ''
          ? physicalAddress.zipCode
          : `${physicalAddress.zipCode} - ${physicalAddress.zipPlus}`;
      } else {
        intlZip = physicalAddress.internationalZipCode;
      }

      physicalAddressZip.value = intlZip || '';
    }
  }
  
  if (mailingAddress) {
    mailingAddress1.value = mailingAddress.addressLine1 || '';
    mailingAddress2.value = mailingAddress.addressLine2 || '';
    mailingAddressCity.value = mailingAddress.city?.toUpperCase() || '';
    mailingAddressState.value = mailingAddress.addressType == 'Us Domestic' ? mailingAddress.state : mailingAddress.province;

    if (mailingAddress.addressType === 'Us Domestic') {
      mailingAddressZip.value = mailingAddress.zipCode || '';
      mailingAddressZipPlus.value = mailingAddress.zipPlus || '';
    } 
    else {
      let intlZip = '';

      if (mailingAddress.addressType === 'Us Domestic') {
        intlZip = mailingAddress.zipPlus === ''
          ? mailingAddress.zipCode
          : `${mailingAddress.zipCode} - ${mailingAddress.zipPlus}`;
      } else {
        intlZip = mailingAddress.internationalZipCode;
      }

      mailingAddressZip.value = intlZip || '';
    }
  }
}

// Lifecycle hook
onMounted(() => {
  window.scrollTo(0, 0);
  document.body.setAttribute("tabindex", "1");
  document.body.focus();
  document.body.removeAttribute("tabindex");
  document.getElementById("app").scrollTop = 0;
  
  nextTick(() => {
    physicalMailingAddressCheck.value = checkSameAddress.value;
  });
});

// Methods 
const checkMailingAddress = () => {
  if (physicalMailingAddressCheck.value) {
    mailingAddress1.value = physicalAddress1.value;
    mailingAddress2.value = physicalAddress2.value;
    mailingAddressCity.value = physicalAddressCity.value;
    mailingStateId.value = physicalStateId.value;
    mailingAddressState.value = physicalAddressState.value;
    mailingAddressZip.value = physicalAddressZip.value;
    mailingAddressZipPlus.value = physicalAddressZipPlus.value;

    mailingAddress1ErrMsg.value = '';
    mailingAddressCityErrMsg.value = '';
    mailingAdressStateErrMsg.value = '';
    mailingAddressZipErrMsg.value = '';
  }
};

const validateAddress = () => {
  if (physicalAddress1.value) {
    physicalAddress1ErrMsg.value = '';
  } else {
    physicalAddress1ErrMsg.value = 'Address line 1 is required';
  }
};

const validateCity = () => {
  if (physicalAddressCity.value) {
    physicalAddressCityErrMsg.value = '';
  } else {
    physicalAddressCityErrMsg.value = 'City is required';
  }
};

const validateState = () => {
  if (physicalStateId.value) {
    physicalAdressStateErrMsg.value = '';
  } else {
    physicalAdressStateErrMsg.value = 'State is required';
  }
};

const validateZip = () => {
  if (physicalAddressZip.value) {
    physicalAddressZipErrMsg.value = '';
  } else {
    physicalAddressZipErrMsg.value = 'Zipcode is required';
  }
};

const validateMailingAddress = () => {
  if ((!mailingAddress1.value) && !physicalMailingAddressCheck.value) {
    mailingAddress1ErrMsg.value = 'Address line 1 is required';
  } else {
    mailingAddress1ErrMsg.value = '';
  }
};

const validateMailingCity = () => {
  if ((!mailingAddressCity.value) && !physicalMailingAddressCheck.value) {
    mailingAddressCityErrMsg.value = 'City is required';
  } else {
    mailingAddressCityErrMsg.value = '';
  }
};

const validateMailingState = () => {
  if ((!mailingStateId.value) && !physicalMailingAddressCheck.value) {
    mailingAdressStateErrMsg.value = 'State is required';
  } else {
    mailingAdressStateErrMsg.value = '';
  }
};

const validateMailingZip = () => {
  if ((!mailingAddressZip.value) && !physicalMailingAddressCheck.value) {
    mailingAddressZipErrMsg.value = 'Zipcode is required';
  } else {
    mailingAddressZipErrMsg.value = '';
  }
};

const changeState = (e) => {
  if (physicalStateId.value) {
    physicalAdressStateErrMsg.value = '';
    physicalAddressCity.value = '';
    physicalAddressZip.value = '';
    physicalAddressZipPlus.value = '';
    physicalAddressState.value = e.target.value;
  } else {
    physicalAdressStateErrMsg.value = 'State is required';
  }
};

const changeMailingState = (e) => {
  if ((!mailingStateId.value) && !physicalMailingAddressCheck.value) {
    mailingAdressStateErrMsg.value = 'State is required';
  } else {
    mailingAdressStateErrMsg.value = '';
    mailingAddressCity.value = '';
    mailingAddressZip.value = '';
    mailingAddressZipPlus.value = '';
    mailingAddressState.value = e.target.value;
  }
};

const clearAll = () => {
  physicalAddress1.value = '';
  physicalAddress2.value = '';
  physicalAddressCity.value = '';
  physicalAddressZip.value = '';
  physicalAddressZipPlus.value = '';

  mailingAddress1.value = '';
  mailingAddress2.value = '';
  mailingAddressCity.value = '';
  mailingAddressZip.value = '';
  mailingAddressZipPlus.value = '';

  physicalAddress1ErrMsg.value = '';
  physicalAddressCityErrMsg.value = '';
  physicalAdressStateErrMsg.value = '';
  physicalAddressZipErrMsg.value = '';

  mailingAddress1ErrMsg.value = '';
  mailingAddressCityErrMsg.value = '';
  mailingAdressStateErrMsg.value = '';
  mailingAddressZipErrMsg.value = '';

  physicalMailingAddressCheck.value = false;

  store.state.guestLogin.physicalAddressMsg = '';
  store.state.guestLogin.mailingAddressMsg = '';
};

const handleRouting = async (info) => {
  if (info === 'previous') {
    router.push({ path: '/businessprofile' });
    return;
  }

  if (info !== 'next') 
  return;

  allValidations();

  if (!isPhysicalAddressValid()) 
  return;

  store.state.guestLogin.physicalMailingAddressCheck = physicalMailingAddressCheck.value;

  if (!physicalMailingAddressCheck.value && !isMailingAddressValid()) {
    return;
  }

  const physicalObj = physicalAddressDataObj();
  const mailingObj = mailingAddressDataObj();

  // loader starts
  store.commit('setState', { property: 'isLoading', value: true });

  await nextTick();

  // API calls
  await store.dispatch(COMMONS.GUEST_UPDATE_BOTH_ADDRESS, {
    physical: physicalObj,
    mailing: mailingObj
  });

  // loader stops after navigation completes
  setTimeout(() => {
    store.commit('setState', { property: 'isLoading', value: false });
  }, 1000);
};

const allValidations = () => {
  checkMailingAddress();
  validateAddress();
  validateCity();
  validateState();
  validateZip();
  validateMailingAddress();
  validateMailingCity();
  validateMailingState();
  validateMailingZip();
};

const isPhysicalAddressValid = () => {
  return !physicalAddress1ErrMsg.value && !physicalAddressCityErrMsg.value && !physicalAdressStateErrMsg.value && !physicalAddressZipErrMsg.value;
};

const isMailingAddressValid = () => {
  return !mailingAddress1ErrMsg.value && !mailingAddressCityErrMsg.value && !mailingAdressStateErrMsg.value && !mailingAddressZipErrMsg.value;
};

const physicalAddressDataObj = () => ({
  addressCategoryId: 1,
  addressLine1: physicalAddress1.value.toUpperCase(),
  addressLine2: physicalAddress2.value.toUpperCase(),
  city: physicalAddressCity.value?.toUpperCase(),
  stateId: physicalStateId.value,
  zipCode: physicalAddressZip.value,
  zipPlus: physicalAddressZipPlus.value
});

const mailingAddressDataObj = () => ({
  addressCategoryId: 2,
  addressLine1: mailingAddress1.value.toUpperCase(),
  addressLine2: mailingAddress2.value.toUpperCase(),
  city: mailingAddressCity.value?.toUpperCase(),
  stateId: mailingStateId.value,
  zipCode: mailingAddressZip.value,
  zipPlus: mailingAddressZipPlus.value
});

// Lifecycle hook
onBeforeUnmount(() => {
  store.state.guestLogin.physicalAddressMsg = '';
  store.state.guestLogin.mailingAddressMsg = '';
});
</script>


<style scoped lang="scss">
.app-address-info{
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;

  h2 {
    font-size: 1rem;
    font-family: "Work Sans", sans-serif;
    margin-bottom: 1rem;
  }
  .app-input-address{
    display: block;
    .app-user-address-info{
      display: flex;
      gap: 1rem;
    }
    .app-user-address{
      margin-bottom: 1rem;
      display: flex;
      .user-address-info{
        width: 100%;
      }
      .user-address-info-city, .user-address-info-state{
        width: 200px !important;
      }
      .user-address-info-zip{
        width: 115px;
      }
      .user-address-info-gray{
        background: lightgray;
      }
      .zip-selection {
        width: 106px !important;
      }
      .user-address-info-zipplus {
        max-width: 100px;
      }
      select {
        .app-custom-select {
          max-width: 90%;
        }
      }
      .diff{
        padding: 0.5rem;
      }
      .add-line{
        width: 100%;
      }
    }
  }
}
.phymailcheck{
  margin: 1rem 0 1rem 0;
  span{
    margin-left: 1rem;
  }
}
.btnClear{
  margin: 1rem 0 1rem 0;
}
.err-msg{
  color: var(--ri-red);
}
.zip-error{
  margin-top: -1rem;
}
button {
  margin-bottom: 1.125em;
  &:not(:last-child) {
    margin-right: 1em;
  }
  &.disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
}
@media only screen and (max-width: 510px) {
  .app-address-info{
    display: flex;
    flex-wrap: wrap;
    gap: 2rem;
    .app-input-address{
      display: block;
      .app-user-address-info{
        display: block;
      }
      .app-user-address{
        margin-bottom: 1rem;
        .user-address-info{
          min-width: 300px;
        }
        .user-address-info-state, .user-address-info-zip{
          display: block;
        }
        .user-address-info-city {
          width: 200px;
        }
        .zip-selection {
          width: 106px !important;
        }
        .user-address-info-zipplus {
          max-width: 100px;
        }
      }
    }
  }
}
</style>